/*
 * Wires the checkpoint journal to the live stores (notes/ai-assistant-phase1.md §3.5):
 * capture before each Agent prompt, restore as one undo step, track project
 * loads/saves, and download a checkpoint as a separate project file.
 */

import { v4 as uuidv4 } from "uuid";
import {
    applyDocumentState,
    captureDocumentState,
    getStorePlain,
    onHistoryBaseline,
} from "@app/store/historyStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState, isEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
import { ccprjFunc } from "@app/lib/ccprj.js";
import { checkpointStorage } from "./checkpointDb.js";
import { decodeText, encodeText } from "./codec.js";
import { buildRestoredState, createJournal, isDetached, summarizeRestore } from "./journal.js";

const journal = createJournal({
    storage: checkpointStorage,
    encode: encodeText,
    decode: decodeText,
    newId: uuidv4,
});

const FINGERPRINT_STORES = ["eventGroupState", "metadataState", "projectState", "speakerState", "markerState", "styleState"];

/** Read-only copy for fingerprinting; unlike captureDocumentState it never flushes pending typing. */
function readDocumentState() {
    return Object.fromEntries(FINGERPRINT_STORES.map((store) => [store, getStorePlain(store)]));
}

onHistoryBaseline(({ name }) => {
    const projectId = projectState.id;
    if (!projectId || typeof indexedDB === "undefined") return;
    journal
        .noteBaseline({ projectId, name, getState: readDocumentState })
        .catch((error) => console.warn("[checkpoints] baseline tracking failed", error));
});

function lockedGroupIds() {
    return eventGroupState
        .map((group, index) => (isEventGroupLocked(index) ? group.id : null))
        .filter(Boolean);
}

/**
 * Checkpoint the current project before an Agent prompt. The state is
 * captured synchronously, so edits made after this call are never included.
 * @param {{ projectId: string, chatId: string, messageId: string, reason?: string }} params
 * @returns {{ id: string, ready: Promise<unknown> }}
 */
export function createCheckpoint({ projectId, chatId, messageId, reason = "prompt" }) {
    const id = uuidv4();
    const state = captureDocumentState();
    const ready = journal.createCheckpoint({ id, projectId, chatId, messageId, reason, state });
    return { id, ready };
}

/**
 * Everything the confirmation dialog needs, or null when the checkpoint has
 * expired (pruned or cleared by the browser).
 * @param {string} checkpointId
 */
export async function prepareRestore(checkpointId) {
    const projectId = projectState.id;
    const [checkpoint, meta, records] = await Promise.all([
        journal.loadCheckpoint(checkpointId),
        journal.getMeta(projectId),
        journal.listCheckpoints(projectId),
    ]);
    const record = records.find((item) => item.id === checkpointId);
    if (!checkpoint || !record) return null;

    const current = captureDocumentState();
    const { state, skippedLockedGroups } = buildRestoredState(current, checkpoint, {
        lockedGroupIds: lockedGroupIds(),
    });
    return {
        checkpointId,
        projectId,
        checkpoint,
        state,
        skippedLockedGroups,
        summary: summarizeRestore(current, state),
        detached: isDetached(record, meta),
        createdAt: record.createdAt,
    };
}

/**
 * Apply a prepared restore as one undo step. A "before restore" checkpoint is
 * written first so the restore itself can be redone from the chat, even after
 * a reload.
 * @param {Awaited<ReturnType<typeof prepareRestore>>} prepared
 * @param {{ chatId: string, name: string }} options
 * @returns {Promise<{ undoCheckpointId: string|null, recorded: boolean }>}
 */
export async function applyRestore(prepared, { chatId, name }) {
    if (projectState.id !== prepared.projectId) {
        throw new Error("The project changed before the restore was applied.");
    }
    const before = createCheckpoint({ projectId: prepared.projectId, chatId, messageId: null, reason: "before_restore" });
    // Recompute against the live state: the user may have edited while the dialog was open.
    const { state } = buildRestoredState(captureDocumentState(), prepared.checkpoint, {
        lockedGroupIds: lockedGroupIds(),
    });
    const recorded = applyDocumentState(state, { name });

    let undoCheckpointId = before.id;
    try {
        await before.ready;
    } catch (error) {
        console.warn("[checkpoints] could not save the pre-restore checkpoint", error);
        undoCheckpointId = null;
    }
    await journal
        .noteRestored({ projectId: prepared.projectId, state: captureDocumentState() })
        .catch((error) => console.warn("[checkpoints] restore tracking failed", error));
    return { undoCheckpointId, recorded };
}

/**
 * Download a checkpoint as its own .ccprj project file, leaving the open
 * project untouched.
 * @param {string} checkpointId
 * @param {{ suffix: string }} options  appended to the project name
 * @returns {Promise<boolean>} false when the checkpoint has expired
 */
export async function downloadCheckpointCopy(checkpointId, { suffix }) {
    const state = await journal.loadCheckpoint(checkpointId);
    if (!state) return false;

    const project = {
        ...state.projectState,
        id: uuidv4(),
        teamId: uuidv4(),
        type: "default",
        rootDir: "personal",
        folderId: false,
        commit: false,
        useLocalStorage: false,
        localPath: null,
        name: `${state.projectState?.name ?? "Project"} ${suffix}`.trim(),
    };
    const projectJson = ccprjFunc.createCcprj({
        project,
        metadata: state.metadataState,
        speakers: state.speakerState,
        issues: state.issueState ?? [],
        markers: state.markerState,
        eventGroups: state.eventGroupState,
        style: state.styleState,
    });

    const blob = new Blob([JSON.stringify(projectJson, null, 4)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${project.name.replace(/[\\/:*?"<>|]+/g, "_")}.ccprj`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
}
